import { useCallback } from "react";
import type { ImageItem } from "../../services/imageUtils";
import { useAsyncStorageState } from "./useAsyncStorageState";

export interface RecentlyViewedItem {
  id: string;
  type: "area" | "model";
  name: string;
  slug?: string;
  area_slug?: string;
  area_id?: string;
  viewedAt: number; // Unix timestamp
  thumbnailUrl?: string; // Legacy/Fallback
  imageVariants?: ImageItem[];
}

const MAX_ITEMS = 20; // Store up to 20 items
const STORAGE_KEY = "microbeta_recently_viewed";

/**
 * Hook to track and manage recently viewed areas and models
 */
export const useRecentlyViewed = () => {
  const [items, setItems] = useAsyncStorageState<RecentlyViewedItem[]>(
    STORAGE_KEY,
    []
  );

  /**
   * Add or update an item in the recently viewed list
   */
  const addItem = useCallback(
    (item: Omit<RecentlyViewedItem, "viewedAt">) => {
      setItems((prevItems) => {
        // Remove existing item with same id and type
        const filtered = prevItems.filter(
          (i) => !(i.id === item.id && i.type === item.type)
        );

        // Add new item at the beginning with current timestamp
        const newItem: RecentlyViewedItem = {
          ...item,
          viewedAt: Date.now(),
        };

        // Keep only MAX_ITEMS
        return [newItem, ...filtered].slice(0, MAX_ITEMS);
      });
    },
    [setItems]
  );

  /**
   * Clear all recently viewed items
   */
  const clearAll = useCallback(() => {
    setItems([]);
  }, [setItems]);

  /**
   * Remove a specific item
   */
  const removeItem = useCallback(
    (id: string, type: "area" | "model") => {
      setItems((prevItems) =>
        prevItems.filter((item) => !(item.id === id && item.type === type))
      );
    },
    [setItems]
  );

  /**
   * Update an existing item's properties without modifying viewedAt or position
   */
  const updateItem = useCallback(
    (
      id: string,
      type: "area" | "model",
      updates: Partial<Omit<RecentlyViewedItem, "id" | "type" | "viewedAt">>
    ) => {
      setItems((prevItems) =>
        prevItems.map((item) =>
          item.id === id && item.type === type ? { ...item, ...updates } : item
        )
      );
    },
    [setItems]
  );

  /**
   * Update related area slugs for all models belonging to a specific area
   */
  const updateModelAreaSlugs = useCallback(
    (areaId: string, newAreaSlug: string) => {
      setItems((prevItems) =>
        prevItems.map((item) =>
          item.type === "model" && item.area_id === areaId // Note: Assuming area_id exists on item if added, otherwise might need another way to link
            ? { ...item, area_slug: newAreaSlug }
            : item
        )
      );
    },
    [setItems]
  );

  /**
   * Get recently viewed items, optionally filtered by type
   */
  const getItems = useCallback(
    (type?: "area" | "model", limit?: number) => {
      let filtered = items;

      if (type) {
        filtered = items.filter((item) => item.type === type);
      }

      if (limit) {
        filtered = filtered.slice(0, limit);
      }

      return filtered;
    },
    [items]
  );

  return {
    items,
    addItem,
    updateItem,
    updateModelAreaSlugs,
    clearAll,
    removeItem,
    getItems,
  };
};
